import { Component, ElementRef, Inject, PLATFORM_ID, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LoginServerService } from '../../../features/authentication/services/login-server.service';
import { ProfilService } from '../service/profil.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from '../service/user.service';
import { PhotoDTO } from '../../../models/PhotoDTO';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { EtablissementService } from '../service/etablissement.service';
import { TranslateService } from '@ngx-translate/core';
import heic2any from 'heic2any';
import { ThemeService } from '../../../core/services/theme.service';
import { EtablissementResponseDTO } from '../../../models/EtablissementResponseDTO';
import { PaieService } from '../service/paie.service';
import { ElevesService } from '../service/eleves.service';
import { EleveDTO } from '../../../models/EleveDTO';
import { PaiementEnfantDTO } from '../../../models/PaiementEnfantDTO';
import { ActivatedRoute } from '@angular/router';
import { ProfileUpdateService } from '../service/profile-update.service';
import { NotificationsService } from '../service/notifications.service';
import { notificationsDTO } from '../gestion-jardin/gestion-messagarie/models/notificationsDTO';

@Component({
  selector: 'app-profil',
  templateUrl: './profil.component.html',
  styleUrls: ['./profil.component.css'], // Fix typo here: styleUrl -> styleUrls
})
export class ProfilComponent {
  role: string | null = null;
  userEmail: string | null = null;
  profileImageUrl: string = '../../../../assets/img/Admin.png';
  private profileImageVersion = 0;
  userProfile: any;
  editProfileForm!: FormGroup;
  private userIdKey = parseInt(localStorage.getItem('userId') as string, 10);
  changePasswordForm!: FormGroup;
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  logoUrl: string = '';
  nom: string = '';

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;

  dropdownVisible = false;
  langDropdownVisible = false;
  private themeSubscription: any;
  
  activeTab: string = 'profile';
  parentEnfants: EleveDTO[] = [];
  paiementsHistory: PaiementEnfantDTO[] = [];
  pendingPaiements: PaiementEnfantDTO[] = [];
  paidPaiements: PaiementEnfantDTO[] = [];
  isPaiementModalVisible = false;
  selectedPaiementId: number | null = null;
  stripeForm!: FormGroup;

  // ─── Notifications ───────────────────────────────────────────────────────
  userNotifications: notificationsDTO[] = [];
  notifLoading = false;
  passwordStrength = 0;

  moisList = [
    { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' }, { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' }, { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' }
  ];
  anneeList = [2024, 2025, 2026, 2027];

  constructor(
    private authProvider: LoginServerService,
    private profilService: ProfilService,
    private userService: UserService,
    private notification: NzNotificationService,
    private fb: FormBuilder,
    private etablissementService: EtablissementService,
    public translate: TranslateService,
    private themeService: ThemeService,
    private paieService: PaieService,
    private elevesService: ElevesService,
    private route: ActivatedRoute,
    private profileUpdateService: ProfileUpdateService,
    private notificationsService: NotificationsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.editProfileForm = this.fb.group({
      nom: ['', [Validators.required, Validators.minLength(2)]],
      prenom: ['', [Validators.required, Validators.minLength(2)]],
      userEmail: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|fr|tn|net|org|edu|gov|co|[a-z]{2})$/)]],
      // Accept 8-digit number with or without +216 prefix (optional for non-Parent roles)
      userPhoneNumber: ['', [Validators.pattern(/^(\+?\d{1,4})?\d{6,12}$/)]],
      // cinParent is optional (Directeurs, Formateurs don't have a parent CIN)
      cinParent: ['', [Validators.pattern(/^\d{8}$/)]],
    });
    this.changePasswordForm = this.fb.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
    });
    this.stripeForm = this.fb.group({
      cardNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{16}$/)]],
      expiry: ['', [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/([0-9]{2})$/)]],
      cvc: ['', [Validators.required, Validators.pattern(/^[0-9]{3,4}$/)]],
      cardholderName: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.route.queryParams.subscribe(params => {
        if (params['tab']) {
          this.activeTab = params['tab'];
        }
      });

      this.loadRole();
      this.loadUserEmail();
      this.loadProfilePhoto(); // Always load the profile photo

      // Optionally load user profile (if needed)
      this.loadProfile();

      if (this.isParent()) {
        this.loadEnfants();
        this.loadPaiements();
      }

      this.loadNotificationsTab();

      // Subscribe to ThemeService for immediate logo/name updates
      this.themeSubscription = this.themeService.etablissement$.subscribe((etab: any) => {
        if (etab) {
          this.nom = etab.nom;
          this.logoUrl = this.themeService.buildLogoUrl(etab.logoUrl);
        } else {
          const storedId = localStorage.getItem('etablissementId');
          if (storedId) {
            this.themeService.loadAndApplyTheme().subscribe();
          }
        }
      });
    }
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  openEditModal(): void {
    this.loadProfile();
  }

  loadProfile() {
    this.profilService.getProfilById(this.userIdKey).subscribe({
      next: (data) => {
        this.userProfile = data;
        this.editProfileForm.patchValue(data); // Populate form
      },
      error: (err) => console.error('Error loading profile:', err),
    });
  }

  /** Strip non-digit chars from phone field for validation */
  private normalizePhoneField(): void {
    const phoneControl = this.editProfileForm.get('userPhoneNumber');
    if (phoneControl) {
      const raw = phoneControl.value || '';
      const cleaned = raw.replace(/[^\d+]/g, '');
      if (cleaned !== raw) {
        phoneControl.setValue(cleaned, { emitEvent: false });
      }
    }
  }

  /** Save profile directly (confirmation overlay not available in this view) */
  openConfirmSaveProfile(): void {
    this.normalizePhoneField();
    if (this.editProfileForm.valid) {
      this.saveProfile();
    } else {
      this.notification.create(
        'error',
        'Formulaire invalide',
        'Veuillez vérifier les champs obligatoires.',
        { nzPlacement: 'bottomLeft', nzDuration: 3000 }
      );
    }
  }

  saveProfile() {
    this.normalizePhoneField();
    if (this.editProfileForm.valid) {
      const formValue = { ...this.editProfileForm.value };
      if (formValue.userPhoneNumber) {
        formValue.userPhoneNumber = formValue.userPhoneNumber.replace(/[^\d]/g, '');
      }
      if (formValue.userPhoneNumber?.startsWith('216')) {
        formValue.userPhoneNumber = formValue.userPhoneNumber.slice(3);
      }
      if (!formValue.userPhoneNumber) {
        delete formValue.userPhoneNumber;
      }
      this.profilService.updateProfil(this.userIdKey, formValue).subscribe({
        next: (response) => {
          this.userProfile = response;
          this.profileUpdateService.emitProfileUpdate({
            prenom: response.prenom,
            nom: response.nom,
            photo: this.profileImageUrl
          });
          this.notification.create(
            'success',
            'Succès',
            'Compte modifié avec succès.',
            { nzPlacement: 'bottomLeft', nzDuration: 3000 }
          );
        },
        error: (err) => {
          console.error('Error updating profile:', err);
          this.notification.create(
            'error',
            'Échec',
            'Une erreur est survenue lors de la modification du compte.',
            { nzPlacement: 'bottomLeft', nzDuration: 3000 }
          );
        },
      });
    }
  }


  loadProfilePhoto(): void {
    const savedImage = localStorage.getItem('profileImage');
    if (savedImage) {
      this.profileImageUrl = savedImage;
      this.profileImageVersion++;
    } else {
      this.userService.getPhotoByUserId(this.userIdKey).subscribe({
        next: (photoUrl: string) => {
          this.profileImageUrl = photoUrl || this.getDefaultAvatar();
          localStorage.setItem('profileImage', this.profileImageUrl);
          this.profileImageVersion++;
        },
        error: (err) => {
          console.error('Error fetching photo:', err);
          this.profileImageUrl = this.getDefaultAvatar();
        },
      });
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const isHeic = file.type === 'image/heic' || file.type === 'image/heif' ||
        file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');

      if (isHeic) {
        heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 })
          .then((convertedBlob) => {
            const jpegFile = new File(
              [convertedBlob as Blob],
              file.name.replace(/\.(heic|heif)$/i, '.jpg'),
              { type: 'image/jpeg' }
            );
            this.uploadPhoto(jpegFile);
          })
          .catch((error) => {
            console.error('Erreur lors de la conversion HEIC:', error);
            this.notification.create('error', 'Erreur', 'Impossible de convertir l\'image HEIC.');
          });
      } else {
        this.uploadPhoto(file);
      }
    }
  }

  deleteAvatar(): void {
    this.userService.deletePhoto(this.userIdKey).subscribe({
      next: () => {
        const defaultAvatar = this.getDefaultAvatar();
        this.profileImageUrl = defaultAvatar;
        localStorage.setItem('profileImage', this.profileImageUrl);
        this.profileImageVersion++;
        this.profileUpdateService.emitProfileUpdate({
          prenom: this.userProfile?.prenom || '',
          nom: this.userProfile?.nom || '',
          photo: this.profileImageUrl
        });
        this.notification.create(
          'success',
          'Succès',
          'Avatar supprimé avec succès.',
        { nzPlacement: 'bottomLeft', nzDuration: 3000 }
      );
    },
    error: (err) => {
      console.error('Error deleting avatar:', err);
      this.notification.create('error', 'Erreur', 'Erreur lors de la suppression de l\'avatar.');
    },
  });
  }

  getProfileImageSrc(): string {
    const url = this.profileImageUrl || this.getDefaultAvatar();
    if (url.startsWith('../../../../assets/')) return url;
    return url + (url.includes('?') ? '&' : '?') + '_t=' + this.profileImageVersion;
  }

  private getDefaultAvatar(): string {
    const role = this.role;
    switch (role) {
      case 'Parent': return '../../../../assets/img/parent.png';
      case 'Formateur': return '../../../../assets/img/formateur1.png';
      case 'Directeur': return '../../../../assets/img/directeur.png';
      default: return '../../../../assets/img/Admin.png';
    }
  }

  uploadPhoto(file: File): void {
    const dto: PhotoDTO = {
      userId: this.userIdKey,
      photo: file,
    };

    this.userService.uploadPhoto(dto).subscribe({
      next: (response: string) => {
        const uploadedPhotoUrl = response.includes('Photo uploaded successfully: ')
          ? response.split('Photo uploaded successfully: ')[1]
          : response; // Fallback: use the entire response if format is unexpected
        this.profileImageUrl = uploadedPhotoUrl || this.getDefaultAvatar();
        localStorage.setItem('profileImage', this.profileImageUrl);
        this.profileImageVersion++;
        this.profileUpdateService.emitProfileUpdate({
          prenom: this.userProfile?.prenom || '',
          nom: this.userProfile?.nom || '',
          photo: this.profileImageUrl
        });
        this.notification.create(
          'success',
          'Succès',
          'Photo mise à jour.',
          { nzPlacement: 'bottomLeft', nzDuration: 3000 }
        );
      },

      error: (err) => {
        console.error('Error uploading photo:', err);
        this.notification.create('error', 'Erreur', 'Erreur lors de la mise à jour de la photo.');
      },
    });
  }



  private loadRole(): void {
    const role = localStorage.getItem('_sid');
    this.role = role;
  }

  private loadUserEmail(): void {
    const userEmail = localStorage.getItem('name');
    this.userEmail = userEmail;
  }

  toggleDropdown(): void {
    this.dropdownVisible = !this.dropdownVisible;
  }

  handleLogout(): void {
    this.authProvider.logout();
  }

  switchLang(lang: string): void {
    this.translate.use(lang);
    localStorage.setItem('lang', lang);
  }

  changePassword(): void {
    if (this.changePasswordForm.valid) {
      const { newPassword, confirmPassword } = this.changePasswordForm.value;

      if (newPassword !== confirmPassword) {
        this.notification.create(
          'error',
          'Erreur',
          'Les mots de passe ne correspondent pas.',
          { nzPlacement: 'bottomLeft', nzDuration: 3000 }
        );
        return;
      }

      // Le modal ne fait que confirmer, on lance ici la modal
      // On ne modifie pas le mot de passe directement
    } else {
      this.notification.create(
        'error',
        'Erreur',
        'Veuillez remplir tous les champs correctement.',
        { nzPlacement: 'bottomLeft', nzDuration: 3000 }
      );
    }
  }




  get passwordMismatch(): boolean {
    const newPassword = this.changePasswordForm.get('newPassword')?.value;
    const confirmPassword = this.changePasswordForm.get('confirmPassword')?.value;
    return newPassword && confirmPassword && newPassword !== confirmPassword;
  }

  togglePasswordVisibility(field: 'old' | 'new' | 'confirm'): void {
    if (field === 'old') {
      this.showOldPassword = !this.showOldPassword;
    } else if (field === 'new') {
      this.showNewPassword = !this.showNewPassword;
    } else if (field === 'confirm') {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }
  confirmChangePassword(): void {
    if (this.changePasswordForm.valid) {
      const userId = parseInt(localStorage.getItem('userId') as string, 10); // Récupération automatique du userId depuis localStorage

      if (!userId) {
        this.notification.create(
          'error',
          'Erreur',
          'UserId introuvable. Veuillez vous reconnecter.',
          { nzPlacement: 'bottomLeft', nzDuration: 3000 }
        );
        return;
      }

      const { oldPassword, newPassword, confirmPassword } = this.changePasswordForm.value;

      if (newPassword !== confirmPassword) {
        this.notification.create(
          'error',
          'Erreur',
          'Les mots de passe ne correspondent pas.',
          { nzPlacement: 'bottomLeft', nzDuration: 3000 }
        );
        return;
      }

      this.userService.changePassword(userId, { oldPassword, newPassword, confirmPassword }).subscribe({
        next: () => {
          this.notification.create(
            'success',
            'Succès',
            'Mot de passe changé avec succès. Déconnexion...',
            { nzPlacement: 'bottomLeft', nzDuration: 3000 }
          );
          this.changePasswordForm.reset();
          // R33: Logout after password change
          setTimeout(() => this.authProvider.logout(), 1500);
        },
        error: () => {
          this.notification.create(
            'error',
            'Erreur',
            'Une erreur est survenue lors du changement de mot de passe.',
            { nzPlacement: 'bottomLeft', nzDuration: 3000 }
          );
        },
      });
    } else {
      this.notification.create(
        'error',
        'Erreur',
        'Veuillez remplir tous les champs correctement.',
        { nzPlacement: 'bottomLeft', nzDuration: 3000 }
      );
    }
  }


  loadEtablissementLogo(): void {
    // This is now handled entirely by ThemeService subscription in ngOnInit
  }
  openConfirmChangePassword(): void {
    this.confirmChangePassword();
  }

  isParent(): boolean {
    return this.role === 'Parent';
  }

  // ─── Notifications Tab ───────────────────────────────────────────────────

  // BUG-008: Add verification action
  openVerification(): void {
    this.notification.create(
      'info',
      'Vérification',
      'Votre compte est vérifié. Aucune action requise.',
      { nzPlacement: 'bottomLeft', nzDuration: 3000 }
    );
  }

  loadNotificationsTab(): void {
    this.notifLoading = true;
    this.notificationsService.getUserNotifications().subscribe({
      next: (data) => {
        this.userNotifications = data;
        this.notifLoading = false;
      },
      error: () => { this.notifLoading = false; }
    });
  }

  get unreadCount(): number {
    return this.userNotifications.filter(n => !n.read).length;
  }

  markNotifRead(notif: notificationsDTO): void {
    if (!notif.read && notif.id) {
      this.notificationsService.markAsRead(notif.id).subscribe({
        next: () => {
          notif.read = true;
        }
      });
    }
  }

  markAllRead(): void {
    const unread = this.userNotifications.filter(n => !n.read && n.id);
    unread.forEach(n => this.markNotifRead(n));
  }

  getNotifIcon(type: string): string {
    if (!type) return '🔔';
    if (type.startsWith('DEMANDE')) return '📄';
    if (type.startsWith('CONGE'))   return '🌴';
    if (type.startsWith('CONTRAT')) return '📋';
    if (type === 'PRESENCE_ABSENT') return '⚠️';
    if (type === 'EVENEMENT')       return '📅';
    if (type === 'SUIVI')           return '👶';
    if (type === 'MENU')            return '🍽️';
    if (type === 'PUBLICATION')     return '📢';
    return '🔔';
  }

  getNotifColorClass(type: string): string {
    if (!type) return 'nc-default';
    if (type.includes('REFUSED') || type.includes('EXPIRE')) return 'nc-danger';
    if (type.includes('ACCEPTED') || type.includes('CREATED') || type.includes('SIGNE')) return 'nc-success';
    if (type === 'PRESENCE_ABSENT') return 'nc-warning';
    return 'nc-info';
  }

  onPasswordStrength(): void {
    const val: string = this.changePasswordForm.get('newPassword')?.value || '';
    let score = 0;
    if (val.length >= 6) score++;
    if (val.length >= 10) score++;
    if (/[A-Z]/.test(val)) score++;
    if (/[0-9]/.test(val)) score++;
    if (/[^A-Za-z0-9]/.test(val)) score++;
    this.passwordStrength = score;
  }

  loadEnfants(): void {
    this.elevesService.getElevesByParentId(this.userIdKey).subscribe({
      next: (enfants) => this.parentEnfants = enfants,
      error: (err) => console.error('Erreur chargement des enfants:', err)
    });
  }

  loadPaiements(): void {
    this.paieService.getPaiementsByParent(this.userIdKey).subscribe({
      next: (paiements) => {
        this.paiementsHistory = paiements;
        this.pendingPaiements = paiements.filter(p => p.statut === 'PENDING' || p.statut === 'EN_ATTENTE');
        this.paidPaiements = paiements.filter(p => p.statut === 'PAID' || p.statut === 'PAYE');
      },
      error: (err) => console.error('Erreur chargement de l\'historique des paiements:', err)
    });
  }

  openStripeModal(paiementId: number | undefined): void {
    if (!paiementId) return;
    this.selectedPaiementId = paiementId;
    this.stripeForm.reset();
    this.isPaiementModalVisible = true;
  }

  closePaiementModal(): void {
    this.isPaiementModalVisible = false;
    this.selectedPaiementId = null;
  }

  soumettrePaiementOnline(): void {
    if (this.stripeForm.valid && this.selectedPaiementId) {
      // In a real scenario, you'd use Stripe.js to tokenize the card.
      // Here we simulate the tokenization and send a mock token to our backend.
      const mockStripeToken = 'tok_' + Math.random().toString(36).substring(7);

      this.paieService.processOnlinePayment(this.selectedPaiementId, mockStripeToken).subscribe({
        next: () => {
          this.notification.success('Paiement réussi', 'Votre paiement a été traité avec succès via Stripe.');
          this.loadPaiements();
          this.closePaiementModal();
        },
        error: (err) => {
          this.notification.error('Erreur de paiement', 'Le paiement a échoué. Veuillez vérifier vos informations.');
          console.error(err);
        }
      });
    } else {
      this.notification.warning('Formulaire invalide', 'Veuillez vérifier les informations de votre carte.');
    }
  }

  genererFactureTest(): void {
    this.paieService.generateTestBilling().subscribe({
      next: () => {
        this.notification.success('Succès', 'Les factures de test ont été générées avec succès !');
        this.loadPaiements();
      },
      error: (err) => {
        this.notification.error('Erreur', 'Impossible de générer les factures de test.');
        console.error(err);
      }
    });
  }

}
